// App.jsx
import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { AppProvider } from "@toolpad/core/react-router-dom";
import { jwtDecode } from "jwt-decode";

// MUI
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  CssBaseline,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";
import { createTheme } from "@mui/material/styles";

// Icons
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PetsIcon from "@mui/icons-material/Pets";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation"; // Assets
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import logo from "./images/bow_icon.png";
import MedicationIcon from "@mui/icons-material/Medication";
const drawerWidth = 240;

function getStoredAccessToken() {
  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken) return null;

  try {
    return JSON.parse(accessToken);
  } catch {
    return accessToken;
  }
}

const theme = createTheme({
  typography: {
    fontFamily: '"IBM Plex Sans Thai", serif',
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          background:
            "linear-gradient(90deg, #c2451f 0%, #F57C00 50%, #FFB74D 100%)",
          color: "#fff",
          boxShadow: "none",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: "#ef771e",
            borderLeft: "4px solid #e04b22",
            "&:hover": {
              backgroundColor: "#ef771e",
            },
          },
        },
      },
    },
  },
});

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const [session, setSession] = useState(null);

  // ✅ แยก state collapse
  const [openMenu, setOpenMenu] = useState({
    service: false,
    pets: false,
  });

  const toggleMenu = (key) => {
    setOpenMenu((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // เปิดเมนูตาม route
  useEffect(() => {
    setOpenMenu({
      service: location.pathname.startsWith("/service"),
      pets: location.pathname.startsWith("/pets"),
    });
  }, [location.pathname]);

  // ตรวจสอบ token
  useEffect(() => {
    const accessToken = getStoredAccessToken();
    const bow_accessToken = localStorage.getItem("bow_accessToken");

    if (!accessToken || !bow_accessToken) {
      navigate("/auth/login");
      return;
    }

    try {
      const decoded = jwtDecode(accessToken);
      const decodedBow = jwtDecode(bow_accessToken);
      const now = Date.now() / 1000;

      if (decoded.exp < now || decodedBow.exp < now) {
        navigate("/auth/login");
      } else {
        setSession({
          user: {
            name: decoded.name,
            lastname: decoded.lastname,
            role:
              decoded.roleId === 1
                ? "โรงพยาบาล"
                : decoded.roleId === 3
                  ? "SUPER_ADMIN"
                  : "สัตวแพทย์",
            roleId: decoded.roleId,
            hospital: decoded.hospital,
          },
        });
      }
    } catch (err) {
      navigate("/auth/login");
    }
  }, [navigate]);

  // ✅ Redirection for SUPER_ADMIN
  useEffect(() => {
    if (session?.user?.role === "SUPER_ADMIN") {
      const allowedPaths = ["/hospitals", "/profile", "/auth"];
      const isAllowed = allowedPaths.some((path) =>
        location.pathname.startsWith(path),
      );

      if (!isAllowed) {
        navigate("/hospitals/list");
      }
    }
  }, [session, location.pathname, navigate]);

  const authentication = useMemo(
    () => ({
      signOut: () => {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("bow_accessToken");
        setSession(null);
        navigate("/auth/login");
      },
    }),
    [navigate],
  );

  // MENU
  const NAVIGATION = useMemo(() => {
    if (!session?.user) return [];

    // 🔥 ถ้าเป็น SUPER_ADMIN ให้เห็นแค่เมนู "จัดการโรงพยาบาล"
    if (session.user.role === "SUPER_ADMIN") {
      return [
        {
          segment: "hospitals/list",
          title: "จัดการโรงพยาบาล",
          icon: <LocalHospitalIcon />,
        },
      ];
    }

    // 🔥 base menu (ของโรงพยาบาล / สัตวแพทย์)
    let baseMenu = [
      { segment: "dashboard", title: "Dashboard", icon: <DashboardIcon /> },
      {
        segment: "service",
        title: "การให้บริการ",
        icon: <LocalHospitalIcon />,
        subMenu: [
          {
            path: "/service/vaccine",
            title: "วัคซีน",
            icon: <VaccinesIcon />,
          },
          {
            path: "/service/parasite",
            title: "การป้องกันพยาธิ",
            icon: <MedicationIcon />,
          },
          {
            path: "/service/microchip",
            title: "ไมโครชิพ",
            icon: <DeveloperBoardIcon />,
          },
        ],
      },
      {
        segment: "pets",
        title: "สัตว์เลี้ยง",
        icon: <PetsIcon />,
        subMenu: [
          {
            path: "/pets/list",
            title: "รายชื่อสัตว์เลี้ยง",
            icon: <PetsIcon />,
          },
          {
            path: "/pets/connection-requests",
            title: "รายการขอเชื่อมต่อ",
            icon: <PersonAddIcon />,
          },
          {
            path: "/pets/history",
            title: "ประวัติการรักษา",
            icon: <MedicalInformationIcon />,
          },
        ],
      },
      {
        segment: "owner/list",
        title: "เจ้าของสัตว์เลี้ยง",
        icon: <PeopleAltIcon />,
      },
      {
        segment: "users",
        title: "จัดการบุคลากร",
        icon: <PeopleAltIcon />,
      },
      {
        segment: "hospitals/list",
        title: "จัดการโรงพยาบาล",
        icon: <LocalHospitalIcon />,
      },
    ];

    // ถ้าเป็นสัตวแพทย์ (roleId === 2) ให้ซ่อนเมนูระดับ admin
    if (session.user.roleId === 2) {
      return baseMenu.filter(
        (item) =>
          item.segment !== "dashboard" &&
          item.segment !== "users" &&
          item.segment !== "hospitals/list",
      );
    }

    return baseMenu;
  }, [session]);

  return (
    <AppProvider
      theme={theme}
      session={session}
      authentication={authentication}
      navigation={NAVIGATION}
    >
      <CssBaseline />

      <Box sx={{ display: "flex" }}>
        {/* Sidebar */}
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              backgroundColor: "#0D1B2A",
              color: "#fff",
            },
          }}
        >
          <Toolbar>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <img src={logo} alt="Logo" width={32} height={32} />
              <Typography variant="h6" sx={{ pl: 1 }}>
                Bow His.
              </Typography>
            </Box>
          </Toolbar>

          <List>
            {NAVIGATION.map((item, idx) => {
              if (item.subMenu) {
                const isOpen = openMenu[item.segment];

                return (
                  <Box key={idx}>
                    <ListItemButton onClick={() => toggleMenu(item.segment)}>
                      <ListItemIcon sx={{ color: "#fff" }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.title} />
                      {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </ListItemButton>

                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.subMenu.map((sub, i) => (
                          <ListItemButton
                            key={i}
                            sx={{ pl: 4 }}
                            selected={location.pathname.startsWith(sub.path)}
                            onClick={() => navigate(sub.path)}
                          >
                            <ListItemIcon sx={{ color: "#fff" }}>
                              {sub.icon}
                            </ListItemIcon>
                            <ListItemText primary={sub.title} />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                );
              }

              return (
                <ListItemButton
                  key={idx}
                  selected={location.pathname.startsWith("/" + item.segment)}
                  onClick={() => navigate("/" + item.segment)}
                >
                  <ListItemIcon sx={{ color: "#fff" }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.title} />
                </ListItemButton>
              );
            })}
          </List>

          {/* Logout */}
          <Box sx={{ position: "absolute", bottom: 0, width: "100%", p: 2 }}>
            <ListItemButton
              onClick={() => navigate("/profile")}
              selected={location.pathname.startsWith("/profile")}
            >
              <ListItemIcon sx={{ color: "#fff" }}>
                <ManageAccountsIcon />
              </ListItemIcon>
              <ListItemText primary="ตั้งค่า" />
            </ListItemButton>

            <ListItemButton onClick={authentication.signOut}>
              <ListItemIcon sx={{ color: "#fff" }}>
                <AccountCircleIcon />
              </ListItemIcon>
              <ListItemText primary="ออกจากระบบ" />
            </ListItemButton>
          </Box>
        </Drawer>

        {/* Main */}
        <Box sx={{ flexGrow: 1, bgcolor: "#f8f9fa", minHeight: "100vh" }}>
          <AppBar
            position="fixed"
            sx={{
              width: `calc(100% - ${drawerWidth}px)`,
              ml: `${drawerWidth}px`,
            }}
          >
            <Toolbar>
              <Box sx={{ flexGrow: 1 }} />
              {session?.user && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AccountCircleIcon fontSize="large" />
                  <Typography fontWeight="bold">
                    {session.user.name} {session.user.lastname} (
                    {session.user.role} สังกัด {session.user.hospital})
                  </Typography>
                </Box>
              )}
            </Toolbar>
          </AppBar>

          <Toolbar />
          <Box sx={{ p: 3 }}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </AppProvider>
  );
}
